import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { X, Filter } from 'lucide-react';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useAllInfluencers } from '@/hooks/useAllInfluencers';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';
import type { PaymentFilterValues } from '../../types/paymentFilter.types';

export type { PaymentFilterValues };

export interface PaymentFiltersProps {
  onClose: () => void;
  onApplyFilters?: (filters: PaymentFilterValues) => void;
}

export const PaymentFilters: React.FC<PaymentFiltersProps> = ({ onClose, onApplyFilters }) => {
  const { userCountry } = useAuth();
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 100000]);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<string>('');
  const [dueDateFrom, setDueDateFrom] = useState<string>('');
  const [dueDateTo, setDueDateTo] = useState<string>('');
  const [hasInvoice, setHasInvoice] = useState<boolean | null>(null);

  // Fetch campaigns and influencers from Supabase
  const { data: campaigns = [], isLoading: loadingCampaigns } = useCampaigns();
  const { data: influencers = [], isLoading: loadingInfluencers } = useAllInfluencers();

  const handleClearAll = () => {
    setInfluencerSearch('');
    setSelectedCampaign('');
    setSelectedStatus('');
    setAmountRange([0, 100000]);
    setSelectedPaymentMode('');
    setDueDateFrom('');
    setDueDateTo('');
    setHasInvoice(null);
  };

  const handleApplyFilters = () => {
    const filters: PaymentFilterValues = {
      influencer: influencerSearch || undefined,
      campaign: selectedCampaign || undefined,
      status: selectedStatus || undefined,
      amountRange,
      paymentMode: selectedPaymentMode || undefined,
      dueDateFrom: dueDateFrom || undefined,
      dueDateTo: dueDateTo || undefined,
      hasInvoice,
    };

    onApplyFilters?.(filters);
    onClose();
  };

  const handleAmountRangeChange = (value: number[]) => {
    if (value.length >= 2) {
      setAmountRange([value[0], value[1]]);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center">
          <Filter className="w-5 h-5 mr-2" />
          Advanced Filters
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Influencer Filter */}
        <div>
          <label className="block text-sm font-medium mb-2">Influencer</label>
          <Input
            placeholder="Search influencer..."
            value={influencerSearch}
            onChange={(e) => setInfluencerSearch(e.target.value)}
          />
        </div>

        {/* Campaign Filter */}
        <div>
          <label className="block text-sm font-medium mb-2">Campaign</label>
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger>
              <SelectValue placeholder={loadingCampaigns ? "Loading campaigns..." : "Select campaign"} />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Payment Status */}
        <div>
          <label className="block text-sm font-medium mb-2">Payment Status</label>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Amount Range */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Amount Range: {formatCurrency(amountRange[0], userCountry)} - {formatCurrency(amountRange[1], userCountry)}
          </label>
          <Slider
            value={amountRange}
            onValueChange={handleAmountRangeChange}
            max={100000}
            step={1000}
            className="mt-2"
          />
        </div>

        {/* Payment Mode */}
        <div>
          <label className="block text-sm font-medium mb-2">Payment Mode</label>
          <Select value={selectedPaymentMode} onValueChange={setSelectedPaymentMode}>
            <SelectTrigger>
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Due Date Range */}
        <div>
          <label className="block text-sm font-medium mb-2">Due Date</label>
          <div className="flex space-x-2">
            <Input
              type="date"
              placeholder="From"
              value={dueDateFrom}
              onChange={(e) => setDueDateFrom(e.target.value)}
            />
            <Input
              type="date"
              placeholder="To"
              value={dueDateTo}
              onChange={(e) => setDueDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Toggle Filters */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center space-x-2">
          <Switch
            checked={hasInvoice === true}
            onCheckedChange={(checked) => setHasInvoice(checked ? true : null)}
          />
          <label className="text-sm">Has Invoice</label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={hasInvoice === false}
            onCheckedChange={(checked) => setHasInvoice(checked ? false : null)}
          />
          <label className="text-sm">Missing Invoice</label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={handleClearAll}>
          Clear All
        </Button>
        <div className="flex space-x-2">
          <Button variant="outline">Save Filter</Button>
          <Button onClick={handleApplyFilters}>Apply Filters</Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFilters;
